import React from 'react';
import { Monitor, Shield, AlertCircle, Laptop } from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';

export function EndpointMonitoring() {
  const { language } = useLanguage();
  
  const content = {
    hero: {
      title: {
        fr: 'Surveillance des Endpoints',
        en: 'Endpoint Monitoring'
      },
      subtitle: {
        fr: 'Protection complète des postes de travail et serveurs',
        en: 'Complete workstation and server protection'
      }
    },
    features: [
      {
        icon: <Shield className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Protection en Temps Réel',
          en: 'Real-time Protection'
        },
        description: {
          fr: 'Détection et blocage instantanés des menaces',
          en: 'Instant threat detection and blocking'
        }
      },
      {
        icon: <Laptop className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Gestion Centralisée',
          en: 'Centralized Management'
        },
        description: {
          fr: 'Administration unifiée de tous les endpoints',
          en: 'Unified administration of all endpoints'
        }
      },
      {
        icon: <Monitor className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Surveillance Comportementale',
          en: 'Behavioral Monitoring'
        },
        description: {
          fr: 'Analyse avancée des comportements suspects',
          en: 'Advanced analysis of suspicious behaviors'
        }
      }
    ],
    capabilities: {
      title: {
        fr: 'Fonctionnalités de Protection',
        en: 'Protection Features'
      },
      items: [
        {
          title: {
            fr: 'Antivirus Avancé',
            en: 'Advanced Antivirus'
          },
          description: {
            fr: 'Protection contre les malwares, ransomwares et autres menaces',
            en: 'Protection against malware, ransomware, and other threats'
          }
        },
        {
          title: {
            fr: 'Contrôle des Applications',
            en: 'Application Control'
          },
          description: {
            fr: 'Gestion des applications autorisées et blocage des programmes malveillants',
            en: 'Management of authorized applications and blocking of malicious programs'
          }
        },
        {
          title: {
            fr: 'Pare-feu Endpoint',
            en: 'Endpoint Firewall'
          },
          description: {
            fr: 'Contrôle granulaire du trafic au niveau de chaque endpoint',
            en: 'Granular traffic control at each endpoint'
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Monitor className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300">
              {content.hero.subtitle[language]}
            </p>
          </div>
        </div>
      </div>

      {/* EDR Dashboard Preview */}
      <div className="container mx-auto px-6 py-12">
        <div className="bg-gray-800/50 rounded-lg overflow-hidden shadow-2xl">
          <img 
            src="https://images.unsplash.com/photo-1593642532744-d377ab507dc8?auto=format&fit=crop&q=80" 
            alt="EDR Statistics Dashboard"
            className="w-full h-auto"
          />
          <div className="p-6">
            <h3 className="text-xl font-bold text-white mb-2">
              {language === 'fr' ? 'Tableau de Bord EDR' : 'EDR Dashboard'}
            </h3>
            <p className="text-gray-300">
              {language === 'fr' 
                ? 'Surveillance avancée des endpoints avec détection et réponse en temps réel.'
                : 'Advanced endpoint monitoring with real-time detection and response.'}
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.features.map((feature, index) => (
            <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-700/50 hover:border-blue-500/50 transition-colors duration-300">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {feature.title[language]}
              </h3>
              <p className="text-gray-300">
                {feature.description[language]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Capabilities Section */}
      <div className="bg-gray-800/50 py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {content.capabilities.title[language]}
          </h2>
          <div className="max-w-4xl mx-auto space-y-8">
            {content.capabilities.items.map((item, index) => (
              <div key={index} className="flex items-start space-x-4 bg-gray-900/50 p-6 rounded-lg">
                <div className="flex-shrink-0 bg-blue-500/10 p-3 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {item.title[language]}
                  </h3>
                  <p className="text-gray-300">
                    {item.description[language]}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1593642532744-d377ab507dc8?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'fr' ? 'Protégez Vos Endpoints' : 'Protect Your Endpoints'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {language === 'fr' 
                ? 'Sécurisez vos postes de travail avec notre solution de surveillance avancée' 
                : 'Secure your workstations with our advanced monitoring solution'}
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              {language === 'fr' ? 'Commencer Maintenant' : 'Get Started Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}